/**
 * Prints a single element's markup via a hidden same-origin iframe.
 *
 * Replaces vue-html-to-paper@1.4.5, which had two defects that between them made printing fail
 * outright:
 *
 *  1. It wrote the print document with `document.write` but never called `document.close()`, so the
 *     document's readyState stayed "loading" forever. The tab spun indefinitely and the browser
 *     deferred the queued `window.print()` until a load that never arrived, so no print dialog
 *     appeared.
 *  2. It printed from a popup (`window.open`) and dereferenced the handle without a null check, so a
 *     blocked popup threw before printing and silently did nothing.
 *
 * An iframe avoids the popup blocker entirely, keeps the operator on the page, and lets us wait on a
 * real load event instead of the plugin's blind 1s timer.
 */

/** Ceiling on waiting for the print document's subresources. Printing is never blocked past this. */
const RESOURCE_LOAD_TIMEOUT_MS = 3000;

/**
 * Last-resort cleanup for a frame whose `afterprint` never fired. Deliberately far longer than any
 * realistic time spent in the print dialog: removing the frame while the dialog is still open
 * produces an empty print job, which is far worse than briefly leaking a 0x0 hidden iframe. The
 * orphan sweep in printHtml is what actually bounds accumulation.
 */
const CLEANUP_BACKSTOP_MS = 300_000;

/** Marks our print frames so a later print can sweep up one that was never cleaned up. */
const PRINT_FRAME_MARKER = 'data-print-frame';

/** Copies the host page's styles so the printed output matches what the operator saw on screen. */
function collectHostStyles(doc: Document): string {
    const nodes = Array.from(doc.querySelectorAll('link[rel="stylesheet"], style'));

    return nodes
        .map(node => {
            if (node.tagName === 'STYLE') {
                return `<style>${node.textContent ?? ''}</style>`;
            }
            const href = (node as HTMLLinkElement).href;
            // Reuse the href rather than the parsed CSSOM: cssRules throws on cross-origin sheets,
            // and the browser serves these from cache since the host page already loaded them.
            return href ? `<link rel="stylesheet" href="${href}">` : '';
        })
        .join('\n');
}

/** Resolves when the element finishes loading, successfully or not. */
function settled(element: HTMLElement): Promise<void> {
    return new Promise<void>(resolve => {
        element.addEventListener('load', () => resolve(), { once: true });
        element.addEventListener('error', () => resolve(), { once: true });
    });
}

/**
 * Resolves once every stylesheet and image in the print document has settled, or once the timeout
 * expires. Resolving on error as well as load is deliberate: an unreachable subresource should
 * degrade the receipt's appearance, never prevent it from printing.
 *
 * Images matter as much as stylesheets here. A delivery-attestation receipt renders the captured
 * signature as an <img> whose src is a base64 data URI, and that still needs a decode pass before
 * it will paint. Waiting only on stylesheets left it to chance: when no sheet is pending — which is
 * the norm under Vite dev, where component CSS arrives as inline <style> — this returned on the
 * next microtask and print() could fire against an undecoded image, silently producing a receipt
 * with a blank signature box.
 */
function waitForResources(frameWindow: Window, timeoutMs: number): Promise<void> {
    const doc = frameWindow.document;

    const pendingSheets = Array.from(
        doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
    ).filter(link => !link.sheet);

    const pendingImages = Array.from(doc.images).filter(image => !image.complete);

    const pending = [...pendingSheets, ...pendingImages].map(settled);

    if (!pending.length) return Promise.resolve();

    return Promise.race([
        Promise.all(pending).then(() => undefined),
        new Promise<void>(resolve => setTimeout(resolve, timeoutMs)),
    ]);
}

/**
 * Writes `html` into a hidden iframe and prints it.
 *
 * @param html      Markup for the print document's body.
 * @param title     Print document title; browsers use it as the default filename for "Save as PDF".
 * @param hostDoc   Document supplying the stylesheets and hosting the iframe.
 */
export async function printHtml(
    html: string,
    title: string,
    hostDoc: Document = document
): Promise<void> {
    // Sweep up a frame from a previous print whose afterprint never fired. Doing this on the way in
    // rather than on a timer is what keeps frames from accumulating without ever risking the removal
    // of one whose dialog is still open.
    hostDoc.querySelectorAll(`iframe[${PRINT_FRAME_MARKER}]`).forEach(stale => stale.remove());

    const frame = hostDoc.createElement('iframe');
    // Kept in the layout (not display:none) so the document lays out and paginates for print, but
    // positioned off-screen at zero size so it is invisible to the operator.
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute(PRINT_FRAME_MARKER, '');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    hostDoc.body.appendChild(frame);

    let cleanedUp = false;
    const cleanUp = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        frame.remove();
    };

    try {
        const frameWindow = frame.contentWindow;
        const frameDoc = frame.contentDocument ?? frameWindow?.document;
        if (!frameWindow || !frameDoc) {
            cleanUp();
            throw new Error('Unable to create the print document.');
        }

        frameDoc.open();
        frameDoc.write(
            `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>` +
                `${collectHostStyles(hostDoc)}</head><body>${html}</body></html>`
        );
        // The fix for defect (1): without this the document never leaves readyState "loading" and
        // the print request is deferred forever.
        frameDoc.close();

        // Tear down when the dialog actually closes. Chrome's print() is modal and returns only
        // once it is dismissed, but Firefox and Safari return immediately — there, a fixed timer
        // would pull the document out from under an open dialog and print a blank page. Registered
        // after close() so the listener cannot be discarded by the document swap.
        frameWindow.addEventListener('afterprint', cleanUp, { once: true });

        await waitForResources(frameWindow, RESOURCE_LOAD_TIMEOUT_MS);

        frameWindow.focus();
        frameWindow.print();
    } finally {
        setTimeout(cleanUp, CLEANUP_BACKSTOP_MS);
    }
}

/**
 * Prints the element with the given id. Signature-compatible with the `$htmlToPaper` it replaces so
 * existing call sites need no changes.
 */
export async function printElementById(
    elementId: string,
    callback?: () => void,
    hostDoc: Document = document
): Promise<void> {
    const element = hostDoc.getElementById(elementId);
    if (!element) {
        throw new Error(`Element to print #${elementId} not found.`);
    }

    await printHtml(element.innerHTML, hostDoc.title, hostDoc);
    callback?.();
}
